import React from 'react';
import { Col, Divider, Tag } from 'antd';
import styled, { css } from 'styled-components';
import { greyColor, maxMobile, maxWidth, maxWidth1300, midWidthApp, minRegularScreen, minWidthApp, primaryColor, primaryFont, primaryHeadingColor, retina, wMedium } from 'Utils/utilsStyle';
import { IsMobile } from 'Utils/UtilityFunctions';
import LazyTooltip, { LazyTooltipBoundary } from 'Components/common/components/LazyTooltip';


type Variant = 'filled' | 'outlined' | 'solid';

type StatusItem = {
  label: string;
  icon: React.ComponentType<any>;
  bgColor: string;
  color: string;
  borderColor: string;
  variant?: Variant;
};

type Props = {
  timeStamp: string;
  status: StatusItem[];
  prevRole?: string;
  company?: string;
  industry?: string;
  variant?: Variant;
  icons?: React.ComponentType<any>[];
  onIconClick?: (e: React.MouseEvent, index?: any) => void;
  hasInsights?: boolean;
};

const tooltipTextStyle = css`
  color: ${primaryHeadingColor};
  font-family: ${primaryFont};
  font-size: 12px;
  font-weight: ${wMedium};
  line-height: 16px;
`

// Custom styled tag that supports variant prop
const StyledTag = styled(Tag) <{ $bgColor: string; $textColor: string; $borderColor: string; $variant: Variant; $isMobile: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 9px;
  border-radius: 16px;
  font-weight: ${wMedium};
  font-size: ${({ $isMobile }) => ($isMobile ? '10px' : '12px')};
  width: 106px;
  
  ${({ $variant, $bgColor, $textColor, $borderColor }) => {
    if ($variant === 'outlined') {
      return `
        background: ${$bgColor} !important;
        border: 1px solid ${$borderColor};
        color: ${$textColor};
      `;
    } else if ($variant === 'solid') {
      return `
        background: ${$bgColor} !important;
        border: 1px solid ${$borderColor};
        color: ${$textColor};
      `;
    } else {
      return `
        background: ${$bgColor} !important;
        border: 1px solid ${$borderColor};
        color: ${$textColor};
      `;
    }
  }}
  
  .ant-tag-icon {
    margin-right: 0;
    display: inline-flex;
    align-items: center;
  }
`;

const NotificationItemsWrapper = styled.div<{ $hasInsights?: boolean; $isMobile?: boolean }>`
  display: ${({ $isMobile }) => $isMobile ? 'grid' : 'flex'};
  align-items: center;
  width: 100%;
  margin-top: ${({ $isMobile }) => $isMobile ? '2px' : '8px'};
  .ant-col {
    padding: 0px !important;
  }
  .ant-col-7 {
    @media all and (min-width: ${minWidthApp}) and (max-width: ${midWidthApp}) {
      flex: 0 0 24%;
      max-width: 24%;
    }
    @media all and (min-width: 1900px) {
      flex: 0 0 22.166667%;
      max-width: 22.166667%;
    }
  }
  .ant-col-17 {
    @media all and (min-width: 1900px) {
      flex: 0 0 77.833333%;
      max-width: 77.833333%;
    }
  }
  .status-tags-container .ant-tag {
    padding: ${({ $isMobile }) => ($isMobile && '0px 9px')};
    font-size: ${({ $isMobile }) => ($isMobile && '8px')};
    height: ${({ $isMobile }) => ($isMobile && '14px')};
    width: ${({ $isMobile }) => ($isMobile ? '80px' : '106px')};
    svg {
      width: ${({ $isMobile }) => ($isMobile && '10px')};
    }
  }
  .user-profile-wrapper {
    gap: ${({ $isMobile, $hasInsights }) => !$isMobile ? $hasInsights ? '12px' : '0px' : '0px'};
  }
  .user-info-wrapper {
    margin-top: ${({ $isMobile }) => $isMobile && '3px'};
  }
  .date {
    font-family: ${primaryFont};
    font-weight: ${wMedium};
    font-size: ${({ $isMobile }) => ($isMobile ? '8px ' : '12px')};
    color: ${greyColor};
  }
  .ant-divider {
    height: 10px;
    border-left: 1px solid #CFCFCF;
    top: 0px;
    margin-left: ${({ $isMobile, $hasInsights }) => ($isMobile ? !$hasInsights ? '4px' : '0px' : '8px')};
    margin-right: ${({ $isMobile, $hasInsights }) => ($isMobile ? !$hasInsights ? '4px' : '0px' : '8px')};
  }
  .user-info-divider {
    margin-inline: 0px;
    padding-right: 16px;
  }
  .additional-info {
    gap: ${({ $isMobile }) => $isMobile ? '8px' : '24px'};
    > span {
      font-family: ${primaryFont};
      font-weight: ${wMedium};
      font-size: 12px;
      color: #6A7282;
      @media all and (max-width: ${maxMobile}) {
        font-size: 8px;
      }
      .ant-tooltip {
        z-index: 1 !important;
        inset: auto !important;
        .ant-tooltip-inner {
          ${tooltipTextStyle}
        }
      }
    }
  }
  .icons {
    gap: ${({ $isMobile, $hasInsights }) => $isMobile ? ($hasInsights ? '8px' : '0px') : ($hasInsights ? '10px' : '0px')};
    flex-shrink: 0;
    .ant-tooltip {
      z-index: 1 !important;
      .ant-tooltip-inner {
        ${tooltipTextStyle}
      }
    }
    svg {
      width: ${({ $isMobile }) => $isMobile && '10px'}
    }
    .sales-insight-icon:hover {
      path {
        fill: ${primaryColor};
      }
    }
    .outreach-message-icon:hover {
      path {
        fill: ${primaryColor};
      }
    }
    .role-challenges-icon {
      height: ${({ $isMobile }) => $isMobile ? '10px' : '13px'}
    }
    .role-challenges-icon:hover {
      path {
        fill: ${primaryColor};
      }
    }
    .engagement-opportunity-icon:hover {
      path {
        fill: ${primaryColor};
      }
    }
  }
`;

const FeedHighlightsNotificationItems = (props: Props) => {
  const { timeStamp, status, prevRole, company, industry, variant = 'filled', icons, onIconClick, hasInsights } = props;
  const quickGilroyInsightIndex = 4;
  const shouldShowIcons = Boolean(hasInsights || icons?.[quickGilroyInsightIndex]);
  const isMobile = IsMobile();

  return (
    <LazyTooltipBoundary>
      <NotificationItemsWrapper $isMobile={isMobile} $hasInsights={hasInsights}>
        <Col span={isMobile ? 24 : 7}>
          <div className='user-profile-wrapper d-flex align-items-center'>
            <div className="status-tags-container">
              {hasInsights && status.map((statusItem, index) => {
                const IconComponent = statusItem.icon;
                const itemVariant = statusItem.variant || variant;

                return (
                  <StyledTag
                    key={index}
                    icon={<IconComponent />}
                    $bgColor={statusItem.bgColor}
                    $textColor={statusItem.color}
                    $borderColor={statusItem.borderColor}
                    $variant={itemVariant}
                    $isMobile={isMobile}
                  >
                    {statusItem.label}
                  </StyledTag>
                );
              })}
            </div>
            <div className='date'>
              {timeStamp}
            </div>
          </div>
        </Col>
        <Col span={isMobile ? 24 : 17}>
          <div className={`user-info-wrapper d-flex align-items-center ${!hasInsights ? 'justify-content-end' : 'justify-content-between'}`}>
            {hasInsights && (<div className='user-info d-flex align-items-center'>
              {!isMobile && <Divider type="vertical" className='user-info-divider' />}
              <div className='additional-info d-flex align-items-center'>
                {prevRole && (
                  <LazyTooltip
                    trigger="hover"
                    placement="bottom"
                    getPopupContainer={(e: HTMLElement) => e}
                    className="feed-highlights-info-tooltip"
                    color="#fff"
                    title={prevRole}
                    arrow={false}
                    zIndex={0}
                  >
                    <span className='prev-role'>Previous Role</span>
                  </LazyTooltip>
                )}
                {company && (
                  <LazyTooltip
                    trigger="hover"
                    placement="bottom"
                    getPopupContainer={(e: HTMLElement) => e}
                    className="feed-highlights-info-tooltip"
                    color="#fff"
                    title={company}
                    arrow={false}
                    zIndex={0}
                  >
                    <span className='company'>Company</span>
                  </LazyTooltip>
                )}
                {industry && (
                  <LazyTooltip
                    trigger="hover"
                    placement="bottom"
                    getPopupContainer={(e: HTMLElement) => e}
                    className="feed-highlights-info-tooltip"
                    color="#fff"
                    title={industry || 'No industry'}
                    arrow={false}
                    zIndex={0}
                  >
                    <span className='industry'>Industry</span>
                  </LazyTooltip>
                )}

              </div>
            </div>
            )}
            {shouldShowIcons && (
              <div className='icons d-flex align-items-center'>
                <Divider type="vertical" />
                {icons?.map((IconComponent, index) => {
                  if (!hasInsights && index !== quickGilroyInsightIndex) {
                    return null;
                  }
                  const getTooltipText = (idx: number) => {
                    switch (idx) {
                      case 0:
                        return 'Sales Angle Insights';
                      case 1:
                        return 'Personalized Outreach';
                      case 2:
                        return 'Role Transition Challenges';
                      case 3:
                        return 'Engagement Opportunities';
                      case 4:
                        return 'Ask Gilroy';
                      default:
                        return 'View insights';
                    }
                  };
                  return (
                    <LazyTooltip
                      key={index}
                      trigger="hover"
                      placement="bottom"
                      getPopupContainer={(e: HTMLElement) => e}
                      color="#fff"
                      title={getTooltipText(index)}
                      arrow={false}
                      zIndex={0}
                    >
                      <div
                        key={index}
                        className="d-flex"
                        onClick={(e) => {
                          e.stopPropagation();
                          onIconClick?.(e, index);
                        }}
                      >
                        <IconComponent />
                      </div>
                    </LazyTooltip>
                  )
                })}
              </div>)}
          </div>
        </Col>
      </NotificationItemsWrapper>
    </LazyTooltipBoundary>
  );
};

export default FeedHighlightsNotificationItems;